import {
  dtoTipoLogradouroPais,
  valoresIniciaisTipoLogradouroPais,
} from 'app/modules/apps/shared/endereco/tipoLogradouroPais'
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  apenasDigitos,
  AtendimentoType,
  GenderType,
  GeneroType,
  OptionsHelper,
  RacaType,
  showAlert,
  showConfirmAlert,
  showError,
} from '../../../../../../_metronic/helpers'
import { useAuth } from '../../../../auth'
import { temVinculoMedicoAtivoNaEmpresa } from 'app/modules/auth/core/vinculos'
import { coletarMensagensErro, FetchState, IOption, useModal } from '../../../../../components'
import {
  EnderecoDtoCreate,
  EnderecoDtoUpdate,
  EnderecoList,
} from '../../../empresa/empresas-list/core/_models'
import {
  PacienteDtoCreate,
  PacienteDtoDadosAdicionais,
  PacienteDtoDadosSaude,
  PacienteDtoUpdate,
  PacienteDtoUpdateAso,
  PacienteView,
} from '../core/_models'
import { setNestedObjectValues, useFormik } from 'formik'
import { editPacienteSchema } from '../components/modal/validation/pacienteSchema'
import {
  createEnderecoPaciente,
  createPaciente,
  deleteAlergiaPaciente,
  deleteDoencaPaciente,
  deleteRemedioPaciente,
  updateAlergiaPaciente,
  updateAsoPaciente,
  updateDadosAdicionaisPaciente,
  updateDoencaPaciente,
  updateEnderecoPaciente,
  updatePaciente,
  updateRemedioPaciente,
  updateSaudePaciente,
} from '../core/_requests'
import { getEmpresaById } from '../../../empresa/empresas-list/core/_requests'
import { getEstadoByUf } from '../../../atendimento_medicos/atendimentos-medico-list/core/_requests'
import {
  getGenerosByName,
  getRacasByName,
} from '../../../../../../_metronic/helpers/crud-helper/_request'
import { IInfo } from '../../../../../components/Modal/SearchPersonModal'
import { useT } from '_metronic/i18n/Metronici18n'
import { CAMPOS_FCI, resolverCampoFormik } from '../core/_fciCampos'
import { condicaoAtendida, ContextoCondicaoFci } from '../core/_fciRegras'
import { ErroTraduzidoFci, lerCadastroIncompleto, traduzirErrosFci } from '../core/_fciErro'
import { OPCOES_JUSTIFICATIVA_NAO_POSSUI_CPF } from '../core/_fciJustificativaCpf'
import { useRegrasFci } from './useRegrasFci'
import { DocumentoPessoa } from 'app/modules/apps/shared/documentos-pessoa/_models'
import {
  dtoDocumentoEstrangeiro,
  temDocumentoEstrangeiro,
  tipoDocumentoEmUso,
  valoresIniciaisIdentificadores,
} from 'app/modules/apps/shared/documentos-pessoa/identificadores'

type UsePacienteModalParams = {
  info?: IInfo
}

/** Códigos do LEDI 8.4.2 para nacionalidade, usados só para escolher qual bloco condicional mostrar. */
const NACIONALIDADE_BRASILEIRA = 1
const NACIONALIDADE_NATURALIZADO = 2
const NACIONALIDADE_ESTRANGEIRO = 3

export const usePacienteModal = ({ info }: UsePacienteModalParams) => {
  const { fetchStates, closeModal, refetch } = useModal()
  const { t } = useT()
  const { currentUser } = useAuth()

  const { data: paciente, isLoading: isPacienteLoading } = (fetchStates['paciente'] ||
    {}) as FetchState<PacienteView>

  const { data: endereco, isLoading: isEnderecoLoading } = (fetchStates['endereco'] ||
    {}) as FetchState<EnderecoList>

  const isMedico = temVinculoMedicoAtivoNaEmpresa(currentUser?.vinculos, paciente?.empresa.id)

  const [isAso, setIsAso] = useState(false)
  const [showEndereco, setShowEndereco] = useState(false)
  const [showAso, setShowAso] = useState(false)
  // Documentos da lista (RG, passaporte, CTPS, BI): passaporte/BI ali já identificam o paciente.
  const [documentosLista, setDocumentosLista] = useState<DocumentoPessoa[]>([])
  const documentoEstrangeiroNaLista = temDocumentoEstrangeiro(documentosLista)

  // Guardrail multi-tenant: as regras do e-SUS são as da empresa **selecionada no formulário**, a
  // mesma que recebe o paciente em `createPaciente`.
  const [empresaIdFci, setEmpresaIdFci] = useState<number | undefined>(undefined)
  const { regras, regrasResolvidas } = useRegrasFci(empresaIdFci)

  /** Valor de raça já gravado, usado como fallback do conjunto completo (Risk 4 do plano). */
  const racaOriginalRef = useRef<RacaType | undefined>(undefined)
  const dadosAdicionaisPopuladosRef = useRef(false)

  /**
   * id da linha de nacionalidade -> código do LEDI (1/2/3).
   *
   * O formulário guarda o **id**; as condições publicadas pelo backend comparam o **código**.
   * Alimentado pelo `onOptionsLoaded` do combo e, na edição, pela própria projeção do paciente
   * (`NacionalidadeView.codigoLedi`) — nenhuma requisição extra.
   */
  const codigosLediRef = useRef<Map<number, number | null>>(new Map())
  /** id do estado -> sigla, porque `GET /municipios/uf/{uf}` é por sigla e o payload grava o id. */
  const siglasEstadoRef = useRef<Map<number, string>>(new Map())
  /** Força re-render quando um dos mapas acima é preenchido de forma assíncrona. */
  const [, setVersaoCatalogos] = useState(0)

  /** Erros do 422 ainda sem destino no formulário (AC7: não somem, vão para o resumo do topo). */
  const [resumoErro, setResumoErro] = useState<{
    mensagem: string
    itens: { rotulo: string; correcao: string }[]
  } | null>(null)
  const errosFciRef = useRef<ErroTraduzidoFci[]>([])

  if (paciente?.dadosAdicionais?.nacionalidade) {
    const { id, codigoLedi } = paciente.dadosAdicionais.nacionalidade
    if (id !== undefined && !codigosLediRef.current.has(id)) {
      codigosLediRef.current.set(id, codigoLedi ?? null)
    }
  }
  if (paciente?.dadosAdicionais?.estadoNascimento) {
    const estado = paciente.dadosAdicionais.estadoNascimento
    if (estado.id !== undefined && estado.sigla && !siglasEstadoRef.current.has(estado.id)) {
      siglasEstadoRef.current.set(estado.id, String(estado.sigla))
    }
  }

  const getPacienteInitialValues = (
    paciente: PacienteView | undefined,
    endereco: EnderecoList | undefined
  ) => {
    const dados = paciente?.dadosAdicionais

    return {
      empresa: paciente?.empresa
        ? {
            label: paciente.empresa.nomeFantasia || '',
            value: paciente.empresa.id,
          }
        : (undefined as IOption<number> | undefined),

      // paciente
      primeiroNome: info?.primeiroNome || paciente?.pessoa?.primeiroNome || '',
      sobreNome: info?.sobreNome || paciente?.pessoa?.sobreNome || '',
      ...valoresIniciaisIdentificadores(
        paciente?.pessoa?.cpf,
        paciente?.pessoa?.cns,
        paciente ? undefined : info?.documento
      ),
      // Telefone unificado: o campo da tela é o celular. Paciente antigo, cadastrado antes da
      // coluna própria, abre com o que estiver em `telefone` — é o mesmo número (V293).
      telefoneCelular: paciente?.pessoa?.telefoneCelular || paciente?.pessoa?.telefone || '',
      email: paciente?.pessoa.usuario?.email || '',
      sexo: paciente?.empresa
        ? {
            label: paciente.pessoa.sexo === GenderType.Feminino ? 'Feminino' : 'Masculino',
            value: paciente.pessoa.sexo,
          }
        : (undefined as IOption<GenderType> | undefined),
      nascimento: info?.dataNascimento || paciente?.pessoa?.dataNascimento || '',
      nomeSocial: paciente?.pessoa?.nomeSocial || '',
      nomeMae: dados?.nomeMae || '',
      desconheceNomeMae: dados?.desconheceNomeMae === true,
      nomeRepresentante: dados?.nomeRepresentante || '',
      raca: undefined as IOption<RacaType> | undefined,
      genero: undefined as IOption<GeneroType> | undefined,
      nacionalidade: dados?.nacionalidade
        ? {
            label: dados.nacionalidade.descricao,
            value: dados.nacionalidade.id,
          }
        : undefined,
      estadoNascimento: dados?.estadoNascimento
        ? {
            label: dados.estadoNascimento.descricao,
            value: dados.estadoNascimento.id,
          }
        : undefined,

      // Campos da Ficha de Cadastro Individual (e-SUS APS)
      municipioNascimento: dados?.municipioNascimento
        ? { label: dados.municipioNascimento.nome, value: dados.municipioNascimento.id }
        : (undefined as IOption<number> | undefined),
      etnia: dados?.etnia
        ? { label: dados.etnia.nome, value: dados.etnia.id }
        : (undefined as IOption<number> | undefined),
      paisNascimento: dados?.paisNascimento
        ? { label: dados.paisNascimento.nome, value: dados.paisNascimento.id }
        : (undefined as IOption<number> | undefined),
      justificativaNaoPossuiCpf: dados?.justificativaNaoPossuiCpf
        ? {
            label:
              OPCOES_JUSTIFICATIVA_NAO_POSSUI_CPF.find(
                (opcao) => opcao.value === dados.justificativaNaoPossuiCpf?.codigo
              )?.label || dados.justificativaNaoPossuiCpf.descricao,
            value: dados.justificativaNaoPossuiCpf.codigo,
          }
        : (undefined as IOption<number> | undefined),
      dtNaturalizacao: dados?.dtNaturalizacao || '',
      portariaNaturalizacao: dados?.portariaNaturalizacao || '',
      dtEntradaBrasil: dados?.dtEntradaBrasil || '',

      altura: paciente?.dadosSaude?.altura || undefined,
      peso: paciente?.dadosSaude?.peso || undefined,
      condicoes: paciente?.dadosSaude?.condicoes || '',
      alergias: paciente?.alergias || [],
      doencas: paciente?.doencas || [],
      medicamentos: paciente?.medicamentos || [],

      // ASO
      rg: {
        numero: paciente?.pessoa?.rg?.numero || '',
        orgao: paciente?.pessoa?.rg?.orgao || '',
        data: paciente?.pessoa?.rg?.data || '',
      },
      ctps: {
        numero: paciente?.pessoa?.ctps?.numero || '',
        serie: paciente?.pessoa?.ctps?.serie || '',
        uf: undefined as IOption<string> | undefined,
      },
      dadosAso: {
        matricula: paciente?.dadosAso?.matricula || '',
        cargo: paciente?.dadosAso?.cargo || '',
        setor: paciente?.dadosAso?.setor || '',
      },

      // endereco
      endereco: {
        cep: endereco?.cep ? endereco.cep : '',
        logradouro: endereco?.logradouro || '',
        numero: endereco?.numero || '',
        complemento: endereco?.complemento || '',
        bairro: endereco?.bairro || '',
        cidade: endereco?.cidade || '',
        uf: undefined as IOption<string> | undefined,
        ...valoresIniciaisTipoLogradouroPais(endereco),
      },
      justificativa: '',
    }
  }

  const initialValues = getPacienteInitialValues(paciente, endereco)

  /**
   * Traduz o estado da tela para o que as condições publicadas observam.
   *
   * Aqui mora a armadilha desta fatia: `values.nacionalidade.value` é o **id da linha** do catálogo,
   * e a condição do backend compara o **código do LEDI** (1/2/3). Comparar id com código faz a tela
   * exigir data de naturalização de quem é brasileiro.
   */
  const resolverContextoCondicao = useCallback(
    (values: any): ContextoCondicaoFci => ({
      codigoLediNacionalidade: values?.nacionalidade?.value
        ? codigosLediRef.current.get(Number(values.nacionalidade.value)) ?? null
        : null,
      raca: values?.raca?.value ?? null,
      semCpf: !values?.cpf,
    }),
    []
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(
      () =>
        editPacienteSchema(
          t,
          showEndereco,
          showAso,
          !!paciente,
          initialValues.cpf,
          initialValues.cns,
          regras,
          resolverContextoCondicao,
          documentoEstrangeiroNaLista
        ),
      [
        t,
        showEndereco,
        showAso,
        paciente,
        initialValues.cpf,
        initialValues.cns,
        regras,
        resolverContextoCondicao,
        documentoEstrangeiroNaLista,
      ]
    ),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      setResumoErro(null)
      errosFciRef.current = []

      // Etapas já persistidas, na ordem em que retornaram 2xx. O `PUT /pacientes/dados/{id}` e o
      // `PUT /pacientes/{id}` são duas requisições **sem atomicidade**: um 422 na segunda deixa a
      // primeira gravada, e a tela conta isso em vez de fingir transação (D3 do plano).
      const etapasConcluidas: string[] = []

      const dadosOriginais = paciente?.dadosAdicionais

      /**
       * CONJUNTO COMPLETO, sempre — inclusive `raca`, `nacionalidade` e `estadoNascimento` que o
       * usuário não tocou.
       *
       * Motivo: `PacienteServiceImpl.java:315-316` **apaga por omissão** no
       * `PUT /pacientes/dados/{id}` (defeito pré-existente, anterior ao e-SUS). Com a regra de
       * obrigatoriedade ligada, ele apagaria o campo e em seguida devolveria 422 pelo que ele mesmo
       * apagou.
       *
       * ISTO PROTEGE A TELA, NÃO CORRIGE A CAUSA: Telekit, API de integração e chamadas manuais
       * continuam expostos ao mesmo apagamento. Follow-up registrado na Dev Evidence da fatia F4.
       *
       * `raca` e `genero` chegam por efeito assíncrono e começam `undefined`; o fallback para o
       * valor original do paciente impede que um submit rápido apague os dois.
       */
      const dadosAdicionais: PacienteDtoDadosAdicionais = {
        // Os campos abaixo saem direto do formulário: todos nascem preenchidos a partir de
        // `paciente.dadosAdicionais` de forma síncrona, então "o que está no formulário" já é o
        // conjunto completo. Fallback aqui seria pior: ressuscitaria um campo condicional que o
        // usuário acabou de deixar de valer (um estrangeiro corrigido para brasileiro voltaria a
        // gravar país de nascimento).
        estadoNascimento: values.estadoNascimento?.value,
        municipioNascimento: values.municipioNascimento?.value,
        nacionalidade: values.nacionalidade?.value,
        nomeMae: values.nomeMae || undefined,
        desconheceNomeMae: values.desconheceNomeMae,
        nomeRepresentante: values.nomeRepresentante || undefined,
        etnia: values.etnia?.value,
        paisNascimento: values.paisNascimento?.value,
        dtNaturalizacao: values.dtNaturalizacao || undefined,
        portariaNaturalizacao: values.portariaNaturalizacao || undefined,
        dtEntradaBrasil: values.dtEntradaBrasil || undefined,
        justificativaNaoPossuiCpf: values.justificativaNaoPossuiCpf?.value,

        // `raca` e `genero` são a exceção: chegam por efeito assíncrono (`GET /racas/{name}`) e
        // começam `undefined`. Sem o fallback, um submit disparado antes da resolução mandaria
        // `undefined` e o backend **apagaria** os dois — exatamente o defeito que o AC6 existe para
        // evitar, reintroduzido por corrida (Risk 4 do plano).
        genero: values.genero?.value ?? dadosOriginais?.genero,
        raca: values.raca?.value ?? racaOriginalRef.current,
      }
      const dadosSaude: PacienteDtoDadosSaude = {
        condicoes: values.condicoes,
        peso: values.peso,
        altura: values.altura,
      }
      let p
      try {
        if (paciente) {
          const documentoAlterado =
            apenasDigitos(values.cpf) !== apenasDigitos(initialValues.cpf) ||
            apenasDigitos(values.cns) !== apenasDigitos(initialValues.cns)
          const pacienteUp: PacienteDtoUpdate = {
            ativo: true,
            email: values.email || undefined,
            pessoa: {
              id: paciente.pessoa.id,
              primeiroNome: values.primeiroNome,
              sobreNome: values.sobreNome,
              cpf: values.cpf || undefined,
              cns: values.cns || undefined,
              ...dtoDocumentoEstrangeiro(values, documentoEstrangeiroNaLista),
              // Um número, duas colunas: `telefone` é o que notificação, prescrição, Memed, Hilab e
              // relatórios leem; `telefoneCelular` é o que a Ficha de Cadastro Individual exige.
              telefone: values.telefoneCelular || undefined,
              telefoneCelular: values.telefoneCelular || undefined,
              sexo: values.sexo?.value as GenderType,
              nascimento: values.nascimento,
              nomeSocial: values.nomeSocial || undefined,
              justificativa: documentoAlterado ? values.justificativa || undefined : undefined,
            },
          }
          if (isMedico) {
            await updateSaudePaciente(Number(paciente.id), dadosSaude)
            etapasConcluidas.push('Dados de saúde')
          }
          await updateDadosAdicionaisPaciente(Number(paciente.id), dadosAdicionais)
          etapasConcluidas.push('Dados adicionais')
          p = await updatePaciente(paciente.id, pacienteUp)
          etapasConcluidas.push('Dados pessoais')

          if (isAso && showAso) {
            const pacienteAso: PacienteDtoUpdateAso = {
              pessoa: {
                id: paciente.pessoa.id,
                rg: { numero: values.rg.numero, orgao: values.rg.orgao, data: values.rg.data },
                ctps: {
                  numero: values.ctps.numero,
                  serie: values.ctps.serie,
                  uf: values.ctps.uf ? values.ctps.uf.value : '',
                },
              },
              dadosAso: {
                cargo: values.dadosAso.cargo,
                setor: values.dadosAso.setor,
                matricula: values.dadosAso.matricula,
              },
            }
            await updateAsoPaciente(p?.id, pacienteAso)
          }
          if (showEndereco) {
            if (endereco) {
              const end: EnderecoDtoUpdate = {
                id: endereco?.id as number,
                cep: values.endereco.cep,
                logradouro: values.endereco.logradouro,
                numero: values.endereco.numero,
                complemento: values.endereco.complemento ? values.endereco.complemento : null,
                bairro: values.endereco.bairro,
                cidade: values.endereco.cidade,
                uf: values.endereco.uf ? values.endereco.uf.value : '',
                ...dtoTipoLogradouroPais(values.endereco),
              }

              await updateEnderecoPaciente(paciente.id, end)
            } else {
              const end: EnderecoDtoCreate = {
                tipo: 1,
                cep: values.endereco.cep,
                logradouro: values.endereco.logradouro,
                numero: values.endereco.numero,
                complemento: values.endereco.complemento ? values.endereco.complemento : null,
                bairro: values.endereco.bairro,
                cidade: values.endereco.cidade,
                uf: values.endereco.uf ? values.endereco.uf.value : '',
                ...dtoTipoLogradouroPais(values.endereco),
              }
              await createEnderecoPaciente(Number(p?.id), end)
            }
          }
        } else {
          const pacienteCreate: PacienteDtoCreate = {
            primeiroNome: values.primeiroNome,
            sobreNome: values.sobreNome,
            cpf: values.cpf || undefined,
            cns: values.cns || undefined,
            email: values.email || undefined,
            // Mesmo número nas duas colunas — ver o comentário no bloco de atualização.
            telefone: values.telefoneCelular || undefined,
            // `enviarNaCriacao` da tabela de `_fciCampos.ts` é o **único** lugar que decide isto.
            ...(CAMPOS_FCI['pessoa.telefoneCelular'].enviarNaCriacao
              ? { telefoneCelular: values.telefoneCelular || undefined }
              : {}),
            ...dtoDocumentoEstrangeiro(values, false),
            sexo: values.sexo?.value as GenderType,
            nascimento: values.nascimento,
            nomeSocial: values.nomeSocial || undefined,
            dadosAdicionais,
            dadosSaude: isMedico ? dadosSaude : undefined,
          }

          p = await createPaciente(formik.values.empresa?.value as number, pacienteCreate)
          etapasConcluidas.push('Cadastro do paciente')
          if (p) {
            if (showEndereco) {
              const endereco: EnderecoDtoCreate = {
                tipo: 1,
                cep: values.endereco.cep,
                logradouro: values.endereco.logradouro,
                numero: values.endereco.numero,
                complemento: values.endereco.complemento ? values.endereco.complemento : null,
                bairro: values.endereco.bairro,
                cidade: values.endereco.cidade,
                uf: values.endereco.uf ? values.endereco.uf.value : '',
                ...dtoTipoLogradouroPais(values.endereco),
              }

              await createEnderecoPaciente(Number(p.id), endereco)
            }

            if (isAso && showAso) {
              const pacienteAso: PacienteDtoUpdateAso = {
                pessoa: {
                  id: p?.pessoa.id,
                  rg: { numero: values.rg.numero, orgao: values.rg.orgao, data: values.rg.data },
                  ctps: {
                    numero: values.ctps.numero,
                    serie: values.ctps.serie,
                    uf: values.ctps.uf ? values.ctps.uf.value : '',
                  },
                },
                dadosAso: {
                  cargo: values.dadosAso.cargo,
                  setor: values.dadosAso.setor,
                  matricula: values.dadosAso.matricula,
                },
              }
              await updateAsoPaciente(p?.id, pacienteAso)
            }
          }
        }

        const pacienteId = Number(p?.id)

        await Promise.all([
          ...values.alergias
            .filter((a) => !a.id && a.descricao)
            .map((alergia) =>
              updateAlergiaPaciente(pacienteId, {
                descricao: alergia.descricao as string,
              })
            ),

          ...values.doencas
            .filter((d) => !d.id && d.descricao)
            .map((doenca) =>
              updateDoencaPaciente(pacienteId, {
                descricao: doenca.descricao as string,
              })
            ),

          ...values.medicamentos
            .filter((m) => !m.id && m.descricao)
            .map((remedio) =>
              updateRemedioPaciente(pacienteId, {
                descricao: remedio.descricao as string,
              })
            ),
        ])

        showAlert({
          title: 'Sucesso!',
          text: `Paciente ${paciente ? 'atualizado' : 'criado'} com sucesso.`,
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        // O 422 de cadastro incompleto tem tratamento próprio: cai no campo certo, é renderizado
        // como texto e **não** fecha o modal. `showError` fica só para o resto.
        if (!tratarCadastroIncompleto(error, values, etapasConcluidas)) {
          showError(error)
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  // O timer de foco não pode sobreviver ao modal: solto, ele focaria um campo de outra instância.
  const focoTimerRef = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(focoTimerRef.current), [])

  /** Leva o foco ao primeiro campo com erro; `name` cobre inputs, `id` cobre os combos. */
  function focarCampo(campoFormik: string) {
    if (typeof document === 'undefined') return
    // Adiado de propósito: enquanto `formik.isSubmitting` é verdadeiro os campos estão `disabled`,
    // e `focus()` em elemento desabilitado não faz nada. O `setTimeout(0)` roda depois do
    // `setSubmitting(false)` do `finally` e do re-render correspondente.
    window.clearTimeout(focoTimerRef.current)
    focoTimerRef.current = window.setTimeout(() => {
      const alvo =
        document.querySelector<HTMLElement>(`[name="${campoFormik}"]`) ||
        document.getElementById(`paciente-${campoFormik}`)
      alvo?.focus()
    }, 0)
  }

  /**
   * Traduz o 422 `CADASTRO_INCOMPLETO` para o formulário. Devolve `false` quando o erro é outro,
   * para quem chamou seguir com o tratamento genérico.
   */
  function tratarCadastroIncompleto(error: any, values: any, etapasConcluidas: string[]): boolean {
    const corpo = lerCadastroIncompleto(error)
    if (!corpo) {
      if (etapasConcluidas.length > 0) {
        // Mesmo sem ser 422, se metade já gravou o usuário precisa saber.
        setResumoErro({
          mensagem: montarMensagemParcial(etapasConcluidas),
          itens: [],
        })
        refetch('paciente')
      }
      return false
    }

    const traduzidos = traduzirErrosFci(corpo.erros, {
      tipoDocumento: tipoDocumentoEmUso(values),
    })
    errosFciRef.current = traduzidos

    traduzidos
      .filter((erro) => erro.campoFormik)
      .forEach((erro) => {
        formik.setFieldError(erro.campoFormik as string, erro.correcao)
        formik.setFieldTouched(erro.campoFormik as string, true, false)
      })

    const primeiro = traduzidos.find((erro) => erro.campoFormik)
    if (primeiro?.campoFormik) focarCampo(primeiro.campoFormik)

    const semDestino = traduzidos
      .filter((erro) => !erro.campoFormik)
      .map((erro) => ({ rotulo: erro.rotulo, correcao: erro.correcao }))

    const mensagemBase =
      'Não foi possível concluir: o cadastro está incompleto para a Ficha de Cadastro Individual do e-SUS.'
    const parcial = etapasConcluidas.length > 0 ? ` ${montarMensagemParcial(etapasConcluidas)}` : ''

    setResumoErro({ mensagem: `${mensagemBase}${parcial}`, itens: semDestino })

    if (etapasConcluidas.length > 0) {
      // Desfecho parcial: o formulário passa a refletir o estado real do servidor. Sem compensação
      // por chamada extra — ela também pode falhar e deixaria um terceiro estado, pior que o segundo.
      refetch('paciente')
    }

    return true
  }

  function montarMensagemParcial(etapasConcluidas: string[]) {
    return (
      `Já foram salvos: ${etapasConcluidas.join(', ')}. O restante não foi salvo — ` +
      'corrija os campos indicados e envie de novo.'
    )
  }

  // Setters do Formik são estáveis entre renders; os efeitos abaixo dependem deles, não do `formik`.
  const { setFieldValue, setFieldError, setFieldTouched } = formik

  // Reaplica os erros do 422 depois de um refetch do paciente: `enableReinitialize` reinicia o
  // formulário e limparia as mensagens que o operador precisa ver.
  useEffect(() => {
    if (errosFciRef.current.length === 0) return
    errosFciRef.current
      .filter((erro) => erro.campoFormik)
      .forEach((erro) => {
        setFieldError(erro.campoFormik as string, erro.correcao)
        setFieldTouched(erro.campoFormik as string, true, false)
      })
  }, [paciente, setFieldError, setFieldTouched])

  useEffect(() => {
    setEmpresaIdFci(formik.values.empresa?.value ? Number(formik.values.empresa.value) : undefined)
  }, [formik.values.empresa?.value])

  useEffect(() => {
    if (!formik.values.empresa) return

    const loadEmpresa = async () => {
      try {
        const emp = await getEmpresaById(Number(formik.values.empresa?.value))
        const hasAso = emp?.tiposAtendimento?.includes(AtendimentoType.Ocupacional)
        setIsAso(!!hasAso)
      } catch (error) {
        showError(error)
      }
    }

    loadEmpresa()
  }, [formik.values.empresa])

  // As UFs (CTPS e endereço) são preenchidas na montagem e ao abrir/fechar as seções, com o paciente
  // e o endereço mais recentes — lidos por ref para a chegada deles não disparar o efeito: ela
  // reinicializa o formulário (`enableReinitialize`) e a seção, ao abrir, repovoa.
  const dadosUfRef = useRef({ ctps: paciente?.pessoa.ctps, endereco })
  dadosUfRef.current = { ctps: paciente?.pessoa.ctps, endereco }

  useEffect(() => {
    const populateUf = async () => {
      const { ctps, endereco } = dadosUfRef.current
      if (ctps) {
        const ufCarteiraTrabalho = await getEstadoByUf(ctps.uf)

        setFieldValue('ctps.uf', {
          label: ufCarteiraTrabalho.descricao,
          value: ufCarteiraTrabalho.sigla,
        })
      }

      if (endereco && endereco.uf) {
        const ufEnd = await getEstadoByUf(endereco.uf)
        setFieldValue('endereco.uf', {
          label: ufEnd.descricao,
          value: ufEnd.sigla,
        })
      }
    }

    populateUf()
  }, [showAso, showEndereco, setFieldValue])

  useEffect(() => {
    // Espera as regras resolverem: é `regras.esusAtivo` que decide se a raça legada "Não informado"
    // pode ser populada (AC5). Popular antes e corrigir depois deixaria o campo preenchido com um
    // valor que o PEC recusa.
    const loadDadosAdicionais = async () => {
      if (dadosAdicionaisPopuladosRef.current) return
      if (!paciente?.dadosAdicionais) return
      // Gate pela empresa **do paciente**, e não por `empresaIdFci`: este último é sincronizado por
      // efeito e fica `undefined` no primeiro render, o que deixaria a raça legada ser populada
      // antes de saber se o e-SUS está ativo.
      if (paciente.empresa?.id && !regrasResolvidas) return

      dadosAdicionaisPopuladosRef.current = true

      if (paciente.dadosAdicionais.genero) {
        const genero = await getGenerosByName(paciente.dadosAdicionais.genero)
        setFieldValue('genero', {
          label: genero.descricao,
          value: genero.name,
        })
      }

      if (paciente.dadosAdicionais.raca) {
        const legadoNaoInformado =
          regras.esusAtivo && paciente.dadosAdicionais.raca === RacaType.Nao_Informado

        if (legadoNaoInformado) {
          // AC5 / Risk 9 da F3: o campo abre **em branco** e a regra do endpoint o marca
          // obrigatório, para o operador corrigir agora em vez de descobrir na fila de envio.
          racaOriginalRef.current = undefined
        } else {
          racaOriginalRef.current = paciente.dadosAdicionais.raca
          const raca = await getRacasByName(paciente.dadosAdicionais.raca)
          setFieldValue('raca', {
            label: raca.descricao,
            value: raca.name,
          })
        }
      }
    }

    loadDadosAdicionais()
  }, [paciente, regrasResolvidas, regras.esusAtivo, empresaIdFci, setFieldValue])

  // --- Campos condicionais (AC4) -------------------------------------------------------------

  const codigoLediSelecionado = formik.values.nacionalidade?.value
    ? codigosLediRef.current.get(Number(formik.values.nacionalidade.value)) ?? null
    : null

  /**
   * Campos que a regra do endpoint torna obrigatórios agora (marcador visual e visibilidade).
   *
   * Derivado a cada render, sem memo: o contexto lê `codigosLediRef`, preenchido de forma assíncrona
   * (o re-render vem de `setVersaoCatalogos`), e um memo só por `formik.values` ficaria desatualizado.
   * O custo é um `forEach` sobre a lista curta de campos obrigatórios.
   */
  const camposObrigatoriosAgora = new Set<string>()
  if (regras.esusAtivo) {
    const contexto = resolverContextoCondicao(formik.values)
    regras.camposObrigatorios.forEach((campo) => {
      if (!condicaoAtendida(campo.condicao, contexto)) return
      const campoFormik = resolverCampoFormik(campo.campo, {
        tipoDocumento: tipoDocumentoEmUso(formik.values),
      })
      if (campoFormik) camposObrigatoriosAgora.add(campoFormik)
    })
  }

  /**
   * Campo que o endpoint exige **agora** é sempre visível, mesmo que a condição local de exibição
   * diga o contrário. Sem isso, uma regra publicada com condição diferente da esperada barraria o
   * submit por um campo que o operador não tem como ver nem preencher — o pior desfecho possível
   * para uma tela que existe justamente para acabar com o 422 opaco.
   */
  const exigidoAgora = (campo: string) => camposObrigatoriosAgora.has(campo)

  const mostrarNaturalizacao =
    codigoLediSelecionado === NACIONALIDADE_NATURALIZADO ||
    exigidoAgora('dtNaturalizacao') ||
    exigidoAgora('portariaNaturalizacao')
  const mostrarEstrangeiro =
    codigoLediSelecionado === NACIONALIDADE_ESTRANGEIRO ||
    exigidoAgora('paisNascimento') ||
    exigidoAgora('dtEntradaBrasil')
  /**
   * Sem e-SUS a tela **não muda**: o estado de nascimento continua sempre visível, como hoje. Com
   * e-SUS ativo ele passa a ser o bloco do cidadão brasileiro (código 1), que é o que a ficha pede.
   */
  const mostrarNascimentoBrasil =
    !regras.esusAtivo ||
    codigoLediSelecionado === null ||
    codigoLediSelecionado === NACIONALIDADE_BRASILEIRA ||
    exigidoAgora('municipioNascimento')
  const mostrarEtnia = formik.values.raca?.value === RacaType.Indigena || exigidoAgora('etnia')
  const mostrarJustificativaCpf =
    (!formik.values.cpf && !!formik.values.cns) || exigidoAgora('justificativaNaoPossuiCpf')

  const ufNascimento = formik.values.estadoNascimento?.value
    ? siglasEstadoRef.current.get(Number(formik.values.estadoNascimento.value))
    : undefined

  /**
   * Sumir da tela e limpar o valor são a **mesma ação**: um campo que deixou de valer não pode sair
   * no payload com o valor antigo (um paciente corrigido de estrangeiro para brasileiro não pode
   * gravar país de nascimento).
   */
  // O efeito roda só quando a visibilidade de um bloco muda; `formik.values` entra por ref para não
  // reexecutar a cada alteração do formulário (uma reinicialização, por exemplo, não dispara limpeza).
  const valuesRef = useRef(formik.values)
  valuesRef.current = formik.values

  useEffect(() => {
    const limparAoDeixarDeValer = (blocos: { visivel: boolean; campos: string[] }[]) => {
      blocos.forEach(({ visivel, campos }) => {
        if (visivel) return
        campos.forEach((campo) => {
          const atual = (valuesRef.current as any)[campo]
          if (atual === undefined || atual === '' || atual === null) return
          setFieldValue(campo, typeof atual === 'string' ? '' : undefined)
        })
      })
    }

    limparAoDeixarDeValer([
      {
        visivel: mostrarNaturalizacao,
        campos: ['dtNaturalizacao', 'portariaNaturalizacao'],
      },
      { visivel: mostrarEstrangeiro, campos: ['paisNascimento', 'dtEntradaBrasil'] },
      { visivel: mostrarNascimentoBrasil, campos: ['estadoNascimento', 'municipioNascimento'] },
      { visivel: mostrarEtnia, campos: ['etnia'] },
      { visivel: mostrarJustificativaCpf, campos: ['justificativaNaoPossuiCpf'] },
    ])
  }, [
    mostrarNaturalizacao,
    mostrarEstrangeiro,
    mostrarNascimentoBrasil,
    mostrarEtnia,
    mostrarJustificativaCpf,
    setFieldValue,
  ])

  // Trocar a UF limpa o município: nunca fica um município de outro estado no payload (AC3).
  // Reexecutar por mudança do município é no-op: a UF anterior continua igual à atual.
  const ufAnteriorRef = useRef<string | undefined>(undefined)
  const municipioNascimento = formik.values.municipioNascimento
  useEffect(() => {
    if (ufAnteriorRef.current !== undefined && ufAnteriorRef.current !== ufNascimento) {
      if (municipioNascimento) setFieldValue('municipioNascimento', undefined)
    }
    ufAnteriorRef.current = ufNascimento
  }, [ufNascimento, municipioNascimento, setFieldValue])

  /** `desconheceNomeMae` marcado desabilita e limpa `nomeMae`, e vice-versa. */
  const alternarDesconheceNomeMae = (marcado: boolean) => {
    formik.setFieldValue('desconheceNomeMae', marcado)
    if (marcado && formik.values.nomeMae) formik.setFieldValue('nomeMae', '')
  }

  const registrarNacionalidades = useCallback((rawData: any[]) => {
    let mudou = false
    ;(rawData || []).forEach((item) => {
      if (item?.id === undefined) return
      const codigo = item.codigoLedi ?? null
      if (codigosLediRef.current.get(item.id) !== codigo) {
        codigosLediRef.current.set(item.id, codigo)
        mudou = true
      }
    })
    if (mudou) setVersaoCatalogos((valor) => valor + 1)
  }, [])

  const registrarEstados = useCallback((rawData: any[]) => {
    let mudou = false
    ;(rawData || []).forEach((item) => {
      if (item?.id === undefined || !item?.sigla) return
      if (siglasEstadoRef.current.get(item.id) !== item.sigla) {
        siglasEstadoRef.current.set(item.id, String(item.sigla))
        mudou = true
      }
    })
    if (mudou) setVersaoCatalogos((valor) => valor + 1)
  }, [])

  /**
   * Raça/cor com "Não informado" fora do caminho quando o e-SUS está ativo — o PEC recusa a ficha
   * sem `racaCorCidadao`.
   *
   * O filtro é **local**: `OptionsHelper.getOptionsRacas` alimenta também o cadastro de funcionário
   * e outros consumidores, que não estão sob a regra da FCI (D4 do plano).
   */
  const fetchOptionsRacas = useCallback(async () => {
    const resposta = await OptionsHelper.getOptionsRacas()
    if (!regras.esusAtivo) return resposta
    return {
      ...resposta,
      options: resposta.options.filter((opcao) => opcao.value !== RacaType.Nao_Informado),
    }
  }, [regras.esusAtivo])

  const handleDeleteAlergia = async (index: number) => {
    const alergia = formik.values.alergias[index]

    if (alergia.id && paciente) {
      try {
        const isConfirmed = await showConfirmAlert()
        if (!isConfirmed) return
        await deleteAlergiaPaciente(Number(paciente.id), alergia.id)
      } catch (error) {
        showError(error)
      }
    }

    const novas = formik.values.alergias.filter((_, i) => i !== index)
    formik.setFieldValue('alergias', novas)
  }

  const handleDeleteDoenca = async (index: number) => {
    const doenca = formik.values.doencas[index]

    if (doenca.id && paciente) {
      try {
        const isConfirmed = await showConfirmAlert()
        if (!isConfirmed) return
        await deleteDoencaPaciente(Number(paciente.id), doenca.id)
      } catch (error) {
        showError(error)
      }
    }

    const novas = formik.values.doencas.filter((_, i) => i !== index)
    formik.setFieldValue('doencas', novas)
  }

  const handleDeleteMedicamento = async (index: number) => {
    const medicamento = formik.values.medicamentos[index]

    if (medicamento.id && paciente) {
      try {
        const isConfirmed = await showConfirmAlert()
        if (!isConfirmed) return
        await deleteRemedioPaciente(Number(paciente.id), medicamento.id)
      } catch (error) {
        showError(error)
      }
    }

    const novas = formik.values.medicamentos.filter((_, i) => i !== index)
    formik.setFieldValue('medicamentos', novas)
  }

  /**
   * A comparação é feita sobre os dígitos: o `IMaskInput` reformata o valor que
   * veio da API no mount (`12345678901` -> `123.456.789-01`) e propaga isso para
   * o Formik, então o confronto literal acusaria alteração sem o usuário ter
   * tocado no campo — e a justificativa obrigatória travava o submit.
   */
  const documentoAlterado =
    !!paciente &&
    (apenasDigitos(formik.values.cpf) !== apenasDigitos(initialValues.cpf) ||
      apenasDigitos(formik.values.cns) !== apenasDigitos(initialValues.cns))

  /**
   * Sem isso o submit vira um no-op silencioso quando o campo reprovado não
   * está renderizado (ex.: CEP com a seção de endereço fechada): o Formik
   * bloqueia o envio e nenhum `ErrorLabel` aparece em tela.
   */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const errors = await formik.validateForm()
    const mensagens = coletarMensagensErro(errors)

    if (mensagens.length > 0) {
      formik.setTouched(setNestedObjectValues(errors, true))
      showAlert({
        title: 'Verifique o formulário',
        icon: 'warning',
        html: `<ul class='text-start'>${mensagens.map((msg) => `<li>${msg}</li>`).join('')}</ul>`,
      })
      return
    }

    formik.handleSubmit(event)
  }

  return {
    formik,
    setDocumentosLista,
    documentoEstrangeiroNaLista,
    paciente,
    endereco,
    isAso,
    isMedico,
    showAso,
    showEndereco,
    setShowAso,
    setShowEndereco,
    isPacienteLoading,
    isEnderecoLoading,
    documentoAlterado,
    handleSubmit,
    closeModal,
    handleDeleteAlergia,
    handleDeleteDoenca,
    handleDeleteMedicamento,
    // e-SUS / Ficha de Cadastro Individual
    esusAtivo: regras.esusAtivo,

    camposObrigatoriosAgora,
    mostrarNaturalizacao,
    mostrarEstrangeiro,
    mostrarNascimentoBrasil,
    mostrarEtnia,
    mostrarJustificativaCpf,
    ufNascimento,
    resumoErro,
    registrarNacionalidades,
    registrarEstados,
    alternarDesconheceNomeMae,
    fetchOptionsRacas,
  }
}
